import { styled } from 'styled-components';
import { Navigate, Route } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import React, { FC, Suspense, useCallback, useEffect } from 'react';
import { useAtomValue } from 'jotai';
import { Axios, Sentry } from '@idk-web/core-utils';
import {
  useNotification,
  ConfirmDialog,
  useDialogs,
  useVisibility,
  useSuspended,
  Box,
  ErrorBoundary,
  ErrorDialog,
} from '@idk-web/core-ui';
import { resendOwnPortalUserValidationEmail, PortalTab } from '@idk-web/api';
import { translateError } from '@/utils/error';
import { portalLoginStatus } from '@/state/portalAuth';
import { SentryRoutes } from '@/sentry';
import { usePortalUser } from '@/hooks/portal/usePortalUser';
import { useCompany } from '@/hooks/portal/useCompany';
import { usePortalLogin } from '@/hooks/portal/login/usePortalLogin';
import { DEFAULT_NOTIFICATION_AUTO_HIDE_MS } from '@/config';
import routes from '@/components/portal/routes';
import ProfileScreen from '@/components/portal/profile/ProfileScreen';
import UnauthenticatedRoute from '@/components/portal/UnauthenticatedRoute';
import Layout from '@/components/portal/Layout';
import ChangePassword from '@/components/portal/ChangePassword';
import AuthenticatedRoute from '@/components/portal/AuthenticatedRoute';
import ScheduledMaintenanceMessage from '@/components/common/ScheduledMaintenanceMessage';
import BrowserSupportMessage from '@/components/common/BrowserSupportMessage';

const ErrorScreen = React.lazy(() => import('@/components/error/ErrorScreen'));
const LoginCallback = React.lazy(
  () => import('@/components/common/auth/LoginCallback'),
);
const EmailValidationScreen = React.lazy(
  () => import('@/components/portal/email/EmailValidationScreen'),
);
const ChangePasswordScreen = React.lazy(
  () => import('@/components/portal/login/ChangePasswordScreen'),
);
const LoginScreen = React.lazy(
  () => import('@/components/portal/login/LoginScreen'),
);
const SsoCallback = React.lazy(
  () => import('@/components/portal/sso/SsoCallback'),
);
const LoginSsoScreen = React.lazy(
  () => import('@/components/portal/sso/LoginSsoScreen'),
);
const SessionExpiredScreen = React.lazy(
  () => import('@/components/portal/sso/SessionExpiredScreen'),
);
const TeleIdScreen = React.lazy(
  () => import('@/components/portal/teleid/TeleIdScreen'),
);
const TeleSignScreen = React.lazy(
  () => import('@/components/portal/telesign/TeleSignScreen'),
);
const SafeMailScreen = React.lazy(
  () => import('@/components/portal/safemail/SafeMailScreen'),
);
const AdminScreen = React.lazy(
  () => import('@/components/portal/admin/AdminScreen'),
);
const NotFoundScreen = React.lazy(
  () => import('@/components/notFound/NotFoundScreen'),
);

const Container = styled(Box).attrs({
  direction: 'vertical',
})``;

const HomeRedirect: FC = () => {
  const company = useCompany();

  switch (company.defaultStartTab) {
    case PortalTab.TELESIGN:
      return <Navigate to={routes.telesign} replace />;
    case PortalTab.SAFEMAIL:
      return <Navigate to={routes.safemail} replace />;
    case PortalTab.ADMIN:
      return <Navigate to={routes.admin} replace />;
    default:
      return <Navigate to={routes.teleid} replace />;
  }
};

const PortalApp: FC = () => {
  const { t } = useTranslation();
  const dialog = useDialogs();
  const notification = useNotification();
  const auth = usePortalLogin();
  const company = useCompany();
  const user = usePortalUser();
  const loginStatus = useAtomValue(portalLoginStatus);
  const visibility = useVisibility();
  const reloadCompany = useCallback(async () => {
    if (loginStatus !== 'AUTHENTICATED' || visibility !== 'visible') {
      return;
    }

    try {
      await company.reload();
      await user.reload();
    } catch (e) {
      if (Axios.isError(e) && e.response && Axios.is4xxResponse(e.response)) {
        await auth.logout();
      } else {
        console.error('Failed to reload company', e);
      }
    }
  }, [loginStatus, visibility]);

  useSuspended(reloadCompany, [reloadCompany]);

  useEffect(() => {
    void reloadCompany();
  }, [reloadCompany]);

  useEffect(() => {
    if (loginStatus === 'AUTHENTICATED' && !user?.hasValidatedEmail) {
      const onOk = async () => {
        try {
          await resendOwnPortalUserValidationEmail();

          notification.show(
            t('portal.email_validation.send.success'),
            'success',
            DEFAULT_NOTIFICATION_AUTO_HIDE_MS,
          );
        } catch (e) {
          Sentry.reportError('Failed to resend the email validation link', {
            error: e,
          });
          dialog.open((props) => (
            <ErrorDialog {...props} text={translateError(t, e)} />
          ));
        }
      };

      const handle = dialog.open((props) => (
        <ConfirmDialog
          {...props}
          onOk={onOk}
          header={t('portal.email_validation.dialog.title')}
          text={t('portal.email_validation.dialog.text')}
          cancelText={t('portal.email_validation.dialog.cancel')}
          okText={t('portal.email_validation.dialog.ok')}
        />
      ));

      return () => handle.close();
    }
  }, [loginStatus, user]);

  if (loginStatus === 'AUTHENTICATED' && user?.mustChangePassword) {
    // Users are required to update their password if not set
    return (
      <Container>
        <Layout>
          <ChangePassword />
        </Layout>
      </Container>
    );
  }

  return (
    <Container>
      <ErrorBoundary fallback={ErrorScreen}>
        <BrowserSupportMessage />
        <ScheduledMaintenanceMessage />
        <Layout>
          <Suspense fallback={null}>
            <SentryRoutes>
              <Route
                path={routes.loginCallback}
                element={<LoginCallback redirect={routes.login} />}
              />
              <Route
                path={routes.loginSsoCallback}
                element={<SsoCallback redirect={routes.login} />}
              />
              <Route
                path={routes.validateEmail}
                element={<EmailValidationScreen />}
              />
              <Route
                path={routes.passwordReset}
                element={<ChangePasswordScreen />}
              />
              <Route
                path={routes.login}
                element={
                  <UnauthenticatedRoute>
                    <LoginScreen />
                  </UnauthenticatedRoute>
                }
              />
              <Route
                path={routes.loginSso}
                element={
                  <UnauthenticatedRoute>
                    <LoginSsoScreen />
                  </UnauthenticatedRoute>
                }
              />
              <Route
                path={routes.sessionExpiredSso}
                element={
                  <UnauthenticatedRoute>
                    <SessionExpiredScreen />
                  </UnauthenticatedRoute>
                }
              />
              <Route
                path={routes.home}
                element={
                  <AuthenticatedRoute>
                    <HomeRedirect />
                  </AuthenticatedRoute>
                }
              />
              <Route
                path={`${routes.teleid}/*`}
                element={
                  <AuthenticatedRoute>
                    <TeleIdScreen />
                  </AuthenticatedRoute>
                }
              />
              <Route
                path={`${routes.telesign}/*`}
                element={
                  <AuthenticatedRoute>
                    <TeleSignScreen />
                  </AuthenticatedRoute>
                }
              />
              <Route
                path={`${routes.safemail}/*`}
                element={
                  <AuthenticatedRoute>
                    <SafeMailScreen />
                  </AuthenticatedRoute>
                }
              />
              <Route
                path={`${routes.admin}/*`}
                element={
                  <AuthenticatedRoute>
                    <AdminScreen />
                  </AuthenticatedRoute>
                }
              />
              <Route
                path={routes.profile}
                element={
                  <AuthenticatedRoute>
                    <ProfileScreen />
                  </AuthenticatedRoute>
                }
              />
              <Route
                path="*"
                element={
                  loginStatus === 'LOGGED_OUT' ? (
                    <Navigate to={routes.login} replace />
                  ) : (
                    <NotFoundScreen />
                  )
                }
              />
            </SentryRoutes>
          </Suspense>
        </Layout>
      </ErrorBoundary>
    </Container>
  );
};

export default PortalApp;
